import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
export function AboutSection() {
  return <section id="about" className="py-24 bg-[#F9F9F9] overflow-hidden scroll-mt-24" data-id="element-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-3">
          {/* Image Side - Staggered Layout */}
          <div className="relative h-[600px] hidden lg:block" data-id="element-4">
            {/* Decorative Background */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#1B4D3E]/5 rounded-full blur-3xl" data-id="element-5" />

            {/* Image 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="absolute top-0 right-0 w-3/4 h-3/5 z-10" data-id="element-6">
              <div className="relative h-full w-full overflow-hidden shadow-2xl border-4 border-white" data-id="element-7">
                <img src="https://cdn.magicpatterns.com/uploads/izqSvmCSUvNkzrL2eW3XQB/WhatsApp_Image_2026-01-16_at_01.09.07_(1).jpg" alt="Flicker Digital Event" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" data-id="element-8" />
                <div className="absolute inset-0 bg-[#1B4D3E]/20 mix-blend-multiply" data-id="element-9" />
              </div>
            </motion.div>

            {/* Image 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2,
            ease: 'easeOut'
          }} className="absolute bottom-0 left-0 w-3/4 h-3/5 z-20" data-id="element-10">
              <div className="relative h-full w-full overflow-hidden shadow-2xl border-4 border-white" data-id="element-11">
                <img src="https://cdn.magicpatterns.com/uploads/dA3z6AbFcWo9bEtdZNNQPF/WhatsApp_Image_2026-01-16_at_01.09.08.jpg" alt="Flicker Digital Team" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" data-id="element-12" />
                <div className="absolute inset-0 bg-[#1B4D3E]/10 mix-blend-multiply" data-id="element-13" />
                {/* Gold Accent Frame */}
                <div className="absolute -bottom-4 -left-4 w-24 h-24 border-l-2 border-b-2 border-[#D4AF37]" data-id="element-14" />
              </div>
            </motion.div>
          </div>

          {/* Mobile Image View (Simplified) */}
          <div className="lg:hidden space-y-4" data-id="element-15">
            <img src="https://cdn.magicpatterns.com/uploads/izqSvmCSUvNkzrL2eW3XQB/WhatsApp_Image_2026-01-16_at_01.09.07_(1).jpg" alt="Flicker Digital Event" className="w-full h-64 object-cover rounded-lg shadow-lg" data-id="element-16" />
            <img src="https://cdn.magicpatterns.com/uploads/dA3z6AbFcWo9bEtdZNNQPF/WhatsApp_Image_2026-01-16_at_01.09.08.jpg" alt="Flicker Digital Team" className="w-full h-64 object-cover rounded-lg shadow-lg" data-id="element-17" />
          </div>

          {/* Text Side */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-18">
            <h2 className="text-[#D4AF37] font-sans font-semibold tracking-[0.2em] uppercase text-sm mb-4" data-id="element-19">
              About Us
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif font-medium text-[#1B4D3E] mb-8 leading-tight" data-id="element-20">
              Turning Brief Sparks into{' '}
              <span className="italic text-[#D4AF37]" data-id="element-21">Lasting Growth</span>
            </h3>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed font-light" data-id="element-22">
              From brand storytelling to performance marketing and media, we
              help ambitious brands connect with the right audiences at the
              right moment. We don't just run campaigns; we build ecosystems
              where creativity meets data to drive measurable impact.
            </p>

            <div className="space-y-6" data-id="element-23">
              {['Insight-led Strategy', 'Creative Storytelling', 'Data-Driven Performance', 'Pan-African Expertise'].map((item, index) => <motion.div key={item} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1 * index,
              duration: 0.5
            }} className="flex items-center group" data-id="element-24">
                  <div className="w-8 h-8 rounded-full border border-[#D4AF37] flex items-center justify-center mr-4 group-hover:bg-[#D4AF37] transition-colors duration-300" data-id="element-25">
                    <CheckCircle2 className="h-5 w-5 text-[#D4AF37] group-hover:text-white transition-colors duration-300" data-id="element-26" />
                  </div>
                  <span className="text-[#1B4D3E] font-medium text-lg" data-id="element-27">
                    {item}
                  </span>
                </motion.div>)}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-28">
              <a href="#team" className="text-[#1B4D3E] font-serif italic text-xl hover:text-[#D4AF37] transition-colors inline-flex items-center group" data-id="element-29">
                Meet the Team
                <span className="ml-4 h-[1px] w-12 bg-[#1B4D3E] group-hover:w-20 group-hover:bg-[#D4AF37] transition-all duration-300" data-id="element-30" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}